import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { SimpleDialog } from 'src/components/modal';
import { lang } from 'src/utils';

import type {
  INavigateBlockerContextProps,
  INavigationActionHandler,
} from './navigateBlocker.interface';

interface NavigateBlockerProviderProps {
  children: ReactNode;
}

export const NavigateBlockerContext =
  createContext<INavigateBlockerContextProps>({
    setModalState: () => {},
    resetModalState: () => {},
  });

export const useNavigateBlockerContext = () =>
  useContext(NavigateBlockerContext);

export const NavigateBlockerProvider = ({
  children,
}: NavigateBlockerProviderProps) => {
  const defaultModalState = useMemo(
    () => ({
      isOpen: false,
      proceedNavigation: () => {},
      resetNavigation: () => {},
    }),
    [],
  );

  const [modalState, setModalState] = useState(defaultModalState);

  const resetModalState = useCallback(() => {
    setModalState(defaultModalState);
  }, [defaultModalState]);

  const handleNavigationAction = useCallback<INavigationActionHandler>(
    (actionFn) => {
      actionFn();

      resetModalState();
    },
    [resetModalState],
  );

  return (
    <NavigateBlockerContext.Provider value={{ setModalState, resetModalState }}>
      {children}
      <SimpleDialog
        modalSize="sm"
        open={modalState.isOpen}
        title={lang.navigateBlockerModal.title}
        subTitle={lang.navigateBlockerModal.subTitle}
        cancelButtonColor="secondary"
        cancelText={lang.navigateBlockerModal.confirmText}
        confirmText={lang.navigateBlockerModal.cancelText}
        onClose={() => handleNavigationAction(modalState.resetNavigation)}
        onClickCancel={() =>
          handleNavigationAction(modalState.proceedNavigation)
        }
        onClickConfirm={() =>
          handleNavigationAction(modalState.resetNavigation)
        }
      />
    </NavigateBlockerContext.Provider>
  );
};
